import { useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * @callback InvalidateKeyFnc
 * @param {string|string[]} key - Query key to invalidate
 * @returns {Promise<void>}
 */

/**
 * @param {Object} params
 * @param {import("@tanstack/react-query").QueryClient} params.queryClient
 * @returns {InvalidateKeyFnc}
 */
export function invalidateKey({ queryClient }) {
	return /** @type {InvalidateKeyFnc} */ (
		(key) => {
			queryClient.invalidateQueries({
				queryKey: /** @type {readonly unknown[]} */ ([key]).flat(),
			});
		}
	);
}

/**
 * @typedef {Object} UseFetchReturn
 * @property {*} data
 * @property {Error} [error]
 * @property {boolean} isLoading
 * @property {boolean} isFetching
 * @property {boolean} isSuccess
 * @property {boolean} isError
 * @property {InvalidateKeyFnc} invalidateKey
 * @property {function} refetch
 */

/**
 * @param {Object} props
 * @param {string[]} props.queryKey - The query key used for caching and deduplication
 * @param {(context: {queryKey: string[]}) => Promise<*>} props.queryFnc - Function that fetches data
 * @param {boolean | (() => boolean)} [props.enabled=true]
 *
 * @returns {UseFetchReturn}
 */
export function useFetch({ queryKey, queryFnc, enabled = true }) {
	const queryClient = useQueryClient();

	const useQueryResult = useQuery({
		queryKey,
		queryFn: queryFnc,
		enabled,
	});

	/** @type {UseFetchReturn} */
	const result = /** @type {any} */ (useQueryResult);
	result.invalidateKey = invalidateKey({ queryClient });

	return result;
}
